import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Tag, DownloadSimple, ArrowsClockwise, Sparkle, Copy, MagnifyingGlass,
  CaretDown, CaretUp, Eye, EyeSlash, Info,
} from "@phosphor-icons/react";
import { toast } from "sonner";
import { api, formatApiError, API } from "../../lib/api";

function fmtDate(iso) {
  if (!iso) return "";
  try {
    return new Date(iso).toLocaleString(undefined, {
      year: "numeric", month: "short", day: "numeric",
      hour: "2-digit", minute: "2-digit",
    });
  } catch { return iso; }
}

// Show the prefix + a fixed number of dots so length is stable and unpredictable.
function maskIsrc(isrc) {
  return "QT3R2-26••••";
}

function StatusPill({ status }) {
  const map = {
    on_air:   "text-emerald-400 bg-emerald-500/10 border-emerald-400/30",
    pending:  "text-amber-300 bg-amber-500/10 border-amber-400/30",
    promoted: "text-emerald-400 bg-emerald-500/10 border-emerald-400/30",
    rejected: "text-zinc-500 bg-ink-800 border-ink-600",
  };
  return (
    <span className={`text-[10px] font-mono uppercase tracking-[0.18em] px-2 py-0.5 rounded border ${map[status] || map.pending}`}>
      {(status || "").replace("_", " ")}
    </span>
  );
}

function IsrcRow({ row, expanded, revealed, onToggleExpand, onToggleReveal, onCopy }) {
  return (
    <>
      <tr className="border-t border-ink-700/60 hover:bg-ink-800/30" data-testid={`isrc-row-${row.isrc}`}>
        <td className="px-3 py-2 font-mono text-zinc-500 tracking-widest" data-testid={`isrc-masked-${row.isrc}`}>
          {maskIsrc(row.isrc)}
        </td>
        <td className="px-3 py-2 font-mono text-zinc-300">{row.rank ? `#${String(row.rank).padStart(2, "0")}` : "—"}</td>
        <td className="px-3 py-2 text-white font-medium">{row.artist_name || "—"}</td>
        <td className="px-3 py-2 text-zinc-300">{row.song_title || "—"}</td>
        <td className="px-3 py-2"><StatusPill status={row.status} /></td>
        <td className="px-3 py-2 text-zinc-500 font-mono">{fmtDate(row.issued_at)}</td>
        <td className="px-3 py-2 text-right">
          <button
            onClick={onToggleExpand}
            className="inline-flex items-center gap-1 px-2 py-1 rounded border border-ink-600 text-zinc-300 hover:text-neon-cyan hover:border-neon-cyan/50 text-[10px] font-mono uppercase tracking-[0.15em]"
            title="View details"
            data-testid={`isrc-view-details-${row.isrc}`}
          >
            <Info size={11} /> {expanded ? "Hide" : "Details"}
            {expanded ? <CaretUp size={10} /> : <CaretDown size={10} />}
          </button>
        </td>
      </tr>
      {expanded && (
        <tr className="bg-ink-900/60 border-t border-ink-700/40">
          <td colSpan={7} className="px-3 pb-4 pt-3" data-testid={`isrc-detail-${row.isrc}`}>
            <div className="rounded-md border border-ink-700 bg-ink-800/40 p-4 space-y-3">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-2 text-xs">
                <div>
                  <span className="text-[10px] font-mono uppercase tracking-[0.2em] text-zinc-500 block mb-0.5">Artist</span>
                  <span className="text-white font-medium">{row.artist_name || "—"}</span>
                </div>
                <div>
                  <span className="text-[10px] font-mono uppercase tracking-[0.2em] text-zinc-500 block mb-0.5">Song</span>
                  <span className="text-zinc-200">{row.song_title || "—"}</span>
                </div>
                <div>
                  <span className="text-[10px] font-mono uppercase tracking-[0.2em] text-zinc-500 block mb-0.5">Slot</span>
                  <span className="text-zinc-200 font-mono">{row.rank ? `#${String(row.rank).padStart(2, "0")}` : "—"}</span>
                </div>
                <div>
                  <span className="text-[10px] font-mono uppercase tracking-[0.2em] text-zinc-500 block mb-0.5">Status</span>
                  <StatusPill status={row.status} />
                </div>
                <div>
                  <span className="text-[10px] font-mono uppercase tracking-[0.2em] text-zinc-500 block mb-0.5">Source</span>
                  <span className="text-zinc-300 font-mono">{row.source}</span>
                </div>
                <div>
                  <span className="text-[10px] font-mono uppercase tracking-[0.2em] text-zinc-500 block mb-0.5">Issued</span>
                  <span className="text-zinc-400 font-mono">{fmtDate(row.issued_at)}</span>
                </div>
              </div>

              <div className="border-t border-ink-700/60 pt-3">
                <span className="text-[10px] font-mono uppercase tracking-[0.2em] text-neon-cyan block mb-2">ISRC · confidential</span>
                {!revealed ? (
                  <button
                    onClick={onToggleReveal}
                    className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded border border-amber-400/40 text-amber-300 bg-amber-500/5 hover:bg-amber-500/15 text-xs font-semibold"
                    data-testid={`isrc-reveal-${row.isrc}`}
                    title="Show the ISRC — kept hidden by default"
                  >
                    <Eye size={13} weight="fill" /> Reveal ISRC
                  </button>
                ) : (
                  <div className="flex items-center flex-wrap gap-2" data-testid={`isrc-revealed-${row.isrc}`}>
                    <span className="inline-flex items-center gap-2 rounded-md border border-neon-cyan/40 bg-neon-cyan/5 px-2.5 py-1 text-sm font-mono tracking-widest text-neon-cyan">
                      <Tag size={12} weight="fill" />
                      {row.isrc}
                    </span>
                    <button
                      onClick={() => onCopy(row.isrc)}
                      className="inline-flex items-center gap-1 px-2 py-1 rounded border border-ink-600 text-zinc-300 hover:text-neon-cyan hover:border-neon-cyan/50 text-[11px]"
                      data-testid={`isrc-copy-${row.isrc}`}
                    >
                      <Copy size={11} /> Copy
                    </button>
                    <button
                      onClick={onToggleReveal}
                      className="inline-flex items-center gap-1 px-2 py-1 rounded border border-ink-600 text-zinc-400 hover:text-neon-magenta hover:border-neon-magenta/40 text-[11px]"
                      data-testid={`isrc-hide-${row.isrc}`}
                    >
                      <EyeSlash size={11} /> Hide
                    </button>
                  </div>
                )}
                <p className="text-[10px] text-zinc-500 mt-2 font-mono">
                  Codes are hidden from plain view — click Details → Reveal ISRC to see the full code (two clicks).
                </p>
              </div>
            </div>
          </td>
        </tr>
      )}
    </>
  );
}

export default function IsrcRegistry() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [backfilling, setBackfilling] = useState(false);
  const [query, setQuery] = useState("");
  const [expandedIsrc, setExpandedIsrc] = useState(null);
  const [revealedIsrcs, setRevealedIsrcs] = useState({});

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/isrc/registry");
      setRows(data?.rows || []);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || e.message);
    } finally {
      setLoading(false);
    }
  }, []);
  useEffect(() => { load(); }, [load]);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return rows;
    return rows.filter((r) =>
      (r.artist_name || "").toLowerCase().includes(q) ||
      (r.song_title || "").toLowerCase().includes(q) ||
      String(r.rank || "").includes(q)
    );
  }, [rows, query]);

  const copyIsrc = async (code) => {
    try { await navigator.clipboard.writeText(code); toast.success(`Copied ${code}`); }
    catch { toast.error("Couldn't copy"); }
  };

  const downloadCsv = () => {
    if (!window.confirm("The CSV includes every ISRC code in plain text. Download only if you need it for label paperwork.")) return;
    window.open(`${API}/admin/isrc/registry.csv`, "_blank");
  };

  const backfill = async () => {
    if (!window.confirm("Generate ISRCs for every slot audio file that doesn't have one yet? This re-uploads the audio with the ISRC baked into its metadata.")) return;
    setBackfilling(true);
    try {
      const { data } = await api.post("/admin/isrc/backfill");
      const p = data?.processed_count || 0;
      const s = data?.skipped_count || 0;
      toast.success(`Backfill done — ${p} stamped, ${s} skipped`);
      await load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || e.message);
    } finally {
      setBackfilling(false);
    }
  };

  const toggleExpand = (code) => {
    setExpandedIsrc((cur) => (cur === code ? null : code));
    // Collapsing also un-reveals for security hygiene.
    if (expandedIsrc === code) {
      setRevealedIsrcs((prev) => ({ ...prev, [code]: false }));
    }
  };

  const toggleReveal = (code) => {
    setRevealedIsrcs((prev) => ({ ...prev, [code]: !prev[code] }));
  };

  return (
    <section data-testid="admin-isrc-registry">
      <div className="flex items-center justify-between flex-wrap gap-3 mb-6">
        <div>
          <p className="text-xs font-bold tracking-[0.3em] text-neon-cyan uppercase mb-2 inline-flex items-center gap-2">
            <Tag size={14} weight="fill" /> ISRC Registry
          </p>
          <h2 className="text-2xl sm:text-3xl font-black tracking-tighter">Every code you've ever issued.</h2>
          <p className="text-sm text-zinc-500 mt-2 max-w-2xl">
            Codes are <span className="text-amber-300 font-semibold">hidden by default</span> — click <span className="text-neon-cyan font-mono">Details</span> then <span className="text-neon-cyan font-mono">Reveal ISRC</span> to see any single code. Each track carries a unique <span className="text-neon-cyan font-mono">QT3R2-26••••</span> stamped into its audio metadata plus label credits (<span className="text-white">Tha UndaGround Shoow</span>) and management (<span className="text-white">Steve Blast</span>).
          </p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <button onClick={load} className="inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-md border border-ink-600 text-zinc-300 hover:text-neon-cyan hover:border-neon-cyan/50 text-xs" data-testid="isrc-reload">
            <ArrowsClockwise size={14} /> Reload
          </button>
          <button onClick={backfill} disabled={backfilling} className="inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-md border border-amber-400/40 text-amber-300 hover:bg-amber-400/10 text-xs disabled:opacity-50" data-testid="isrc-backfill">
            <Sparkle size={14} weight="fill" /> {backfilling ? "Backfilling…" : "Backfill missing"}
          </button>
          <button onClick={downloadCsv} className="inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-md bg-neon-cyan text-black text-xs font-semibold hover:bg-neon-cyanHover" data-testid="isrc-download-csv">
            <DownloadSimple size={14} weight="bold" /> Download CSV
          </button>
        </div>
      </div>

      <div className="mb-3 relative">
        <MagnifyingGlass size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-500" />
        <input
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder="Search by artist, song, or slot number…"
          className="w-full pl-9 pr-3 py-2 rounded-md bg-ink-900 border border-ink-600 text-white text-sm placeholder:text-zinc-600 focus:outline-none focus:border-neon-cyan/60"
          data-testid="isrc-search"
        />
      </div>

      <div className="rounded-xl border border-ink-600 bg-ink-900/40 overflow-hidden">
        {loading ? (
          <div className="px-4 py-6 text-zinc-500 text-sm">Loading…</div>
        ) : filtered.length === 0 ? (
          <div className="px-4 py-10 text-center text-zinc-500 text-sm" data-testid="isrc-empty">
            {rows.length === 0 ? "No ISRCs issued yet. Upload an audio file to a slot to auto-generate one." : "No matches for that search."}
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-xs">
              <thead>
                <tr className="text-[10px] font-mono uppercase tracking-[0.18em] text-zinc-500 bg-ink-800/60">
                  <th className="text-left px-3 py-2">ISRC (hidden)</th>
                  <th className="text-left px-3 py-2">Slot</th>
                  <th className="text-left px-3 py-2">Artist</th>
                  <th className="text-left px-3 py-2">Song</th>
                  <th className="text-left px-3 py-2">Status</th>
                  <th className="text-left px-3 py-2">Issued</th>
                  <th className="text-right px-3 py-2">Info</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map((r) => (
                  <IsrcRow
                    key={r.isrc}
                    row={r}
                    expanded={expandedIsrc === r.isrc}
                    revealed={!!revealedIsrcs[r.isrc]}
                    onToggleExpand={() => toggleExpand(r.isrc)}
                    onToggleReveal={() => toggleReveal(r.isrc)}
                    onCopy={copyIsrc}
                  />
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
      <p className="mt-3 text-[10px] font-mono text-zinc-500" data-testid="isrc-total">
        {filtered.length} of {rows.length} ISRC{rows.length === 1 ? "" : "s"} issued · codes masked
      </p>
    </section>
  );
}
